import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Heading from 'components/atoms/Heading';
import Paragraph from 'components/atoms/Paragraph';
import Loading from 'components/atoms/Loading';
import File from 'components/molecules/File';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { database } from 'helpers/firebase';
import { ReactComponent as Close } from 'assets/icons/close.svg';
import { ReactComponent as Search } from 'assets/icons/search.svg';
import { ReactComponent as Upload } from 'assets/icons/upload.svg';

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.08);
  z-index: 1;
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-content: flex-start;
  align-items: flex-start;
  background-color: ${({ theme }) => theme.gray5};
  min-width: 560px;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.16);
`;

const Header = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 25px;
  width: 100%;
`;

const ButtonIcon = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background-color: transparent;
  cursor: pointer;
  fill: ${({ theme }) => theme.textSecondary};
  height: 48px;
  width: 48px;
  border-radius: 50%;
  outline: none;

  :hover {
    background-color: ${({ theme }) => theme.gray4};
    fill: ${({ theme }) => theme.text};
  }

  svg {
    height: 32px;
    width: 32px;
  }
`;

const Files = styled.div`
  display: flex;
  flex-flow: column nowrap;
  height: 600px;
  width: 100%;
  overflow-y: scroll;

  ::-webkit-scrollbar {
    width: 12px;
    background-color: ${({ theme }) => theme.gray5};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.gray4};
    border-radius: 2rem;
  }
`;

const SearchLabel = styled.label`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  background-color: ${({ theme }) => theme.gray3};
  height: 32px;
  width: 100%;
  border-radius: 5px;
  padding: 0 0 0 5px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  margin-right: 15px;
  transition: width 0.3s ease-in-out;

  :focus-within {
    background-color: ${({ theme }) => theme.gray2};
  }

  svg {
    height: 24px;
    width: auto;
    fill: ${({ theme }) => theme.textSecondary};
    margin-right: 5px;
  }
`;

const SearchInput = styled.input`
  border: none;
  height: 100%;
  width: 100%;
  background-color: transparent;
  color: ${({ theme }) => theme.text};
  font-size: ${({ theme }) => theme.body};
  font-family: 'Poppins', sans-serif;
  outline: none;

  ::placeholder {
    color: ${({ theme }) => theme.textSecondary};
    font-size: ${({ theme }) => theme.body};
    font-family: 'Poppins', sans-serif;
  }
`;

const Row = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const UploadButton = styled.label`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  background-color: ${({ theme }) => theme.blue};
  height: 32px;
  border-radius: 5px;
  padding: 0 10px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
  text-decoration: none;

  :hover {
    background-color: ${({ theme }) => theme.blueHover};
    cursor: pointer;
  }

  svg {
    height: 24px;
    width: auto;
    fill: ${({ theme }) => theme.text};
    margin-right: 10px;
  }
`;

function FileModal({ setIsFileModalVisible, files, id }) {
  const modalRef = useRef(null);

  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [checked, setChecked] = useState([]);

  useOutsideClick(modalRef, () => setIsFileModalVisible(false));

  useEffect(() => {
    setIsLoading(false);
    const arr = [];

    const ref = database.ref(`tasks/${id}/files`);

    ref.once('value').then((snapshot) => {
      if (snapshot.exists()) {
        const obj = snapshot.val();

        Object.keys(obj).forEach((key) => {
          arr.push(obj[key]);
        });
      }

      setChecked(arr);
    });
  }, [files, id]);

  const handleSave = () => {
    setIsFileModalVisible(false);

    const ref = database.ref(`tasks/${id}/files`);

    ref.set(checked);
  };

  return (
    <Background>
      <Wrapper ref={modalRef}>
        <Header>
          <Row>
            <Heading>Wybierz pliki</Heading>
            <ButtonIcon type="button" onClick={() => setIsFileModalVisible(false)}>
              <Close />
            </ButtonIcon>
          </Row>
          <Row>
            {checked.length ? (
              <Paragraph>
                {checked.length === 1
                  ? '1 zaznaczony plik'
                  : `Zaznaczonych plików: ${checked.length}`}
              </Paragraph>
            ) : null}
            <UploadButton type="button" onClick={handleSave}>
              <Paragraph>Zapisz</Paragraph>
            </UploadButton>
          </Row>
          <Row>
            <SearchLabel>
              <Search />
              <SearchInput
                type="text"
                placeholder="Szukaj"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </SearchLabel>
            <UploadButton as={Link} to="/przeslij">
              <Upload />
              <Paragraph>Prześlij</Paragraph>
            </UploadButton>
          </Row>
        </Header>
        {isLoading ? (
          <Loading />
        ) : (
          <Files>
            {files
              .filter(({ name }) => name.toLowerCase().includes(query.toLowerCase()))
              .map(({ name, type, key }, i) => (
                <File
                  name={name}
                  id={key}
                  type={type}
                  key={key}
                  delay={i * 0.02}
                  setChecked={setChecked}
                  checked={checked.includes(key)}
                />
              ))}
          </Files>
        )}
      </Wrapper>
    </Background>
  );
}

FileModal.propTypes = {
  setIsFileModalVisible: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      folder: PropTypes.string,
      key: PropTypes.string,
      lastModified: PropTypes.number,
      uploaded: PropTypes.number,
      name: PropTypes.string,
      size: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  id: PropTypes.string,
};

FileModal.defaultProps = {
  files: [],
  id: null,
};

export default FileModal;
